import { ref, computed } from 'vue';
import { ApiClientError, type TimesheetRow } from '../api/client';
import type { DraftResponse } from '../api/draft';
import { importTimesheetCsv } from '../api/timesheet';
import { exportCsv, planImport, templateCsv, type ImportedEntry, type ImportResponse } from '../utils/timesheetCsv';

export interface UseCsvTransferOptions {
  effectiveDraft: () => DraftResponse | null;
  vpRows: () => TimesheetRow[];
  activePeriodStart: () => string;
  activePeriodEnd: () => string;
  applyImport: (entries: ImportedEntry[], onClash: 'skip' | 'replace') => void;
}

// CSV export and import for the period on screen (#6961,
// docs/design/csv-import-export.md).
export function useCsvTransfer(opts: UseCsvTransferOptions) {

  function exportText(): string {
    return exportCsv({
      periodStart: opts.activePeriodStart(),
      vpRows: opts.vpRows(),
      draftRows: opts.effectiveDraft()?.rows ?? [],
    });
  }

  // The byte-order mark is what makes Excel read the file as UTF-8 rather than
  // the machine's code page; the import strips it.
  function download(text: string, fileName: string): void {
    const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  function downloadCsv(): void {
    download(exportText(), `timesheet-${opts.activePeriodEnd()}.csv`);
  }

  function downloadTemplate(): void {
    download(templateCsv(), 'timesheet-template.csv');
  }

  const importOpen = ref(false);
  const importLoading = ref(false);
  const importFileError = ref<string | null>(null);
  const importResult = ref<ImportResponse | null>(null);

  const importPlan = computed(() => planImport(
    importResult.value?.entries ?? [],
    opts.effectiveDraft()?.rows ?? [],
    opts.vpRows(),
  ));
  const importErrors = computed(() => importResult.value?.errors ?? []);
  const importIgnoredVpRows = computed(() => importResult.value?.ignoredVpRows ?? 0);
  const importTotalHours = computed(() => importPlan.value.importing.reduce((sum, e) => sum + e.hours, 0));
  const importClashCount = computed(() => importPlan.value.existing.reduce((n, c) => n + c.days, 0));
  const importClashHours = computed(() => importPlan.value.existing.reduce((h, c) => h + c.hours, 0));

  async function importFile(file: File): Promise<void> {
    importResult.value = null;
    importFileError.value = null;
    importOpen.value = true;
    importLoading.value = true;
    try {
      importResult.value = await importTimesheetCsv(opts.activePeriodEnd(), await file.text());
    } catch (e) {
      // The server's reason for rejecting a file whole (size, header, period)
      // is shown as it is. Anything else — network down, a reply that is not
      // JSON — must not leave the dialog reading as an empty file.
      if (e instanceof ApiClientError) {
        importFileError.value = e.message;
      } else {
        console.error('[CsvImport] import request failed', e);
        importFileError.value = 'The file could not be checked. Nothing was imported — try again.';
      }
    } finally {
      importLoading.value = false;
    }
  }

  function applyImportPlan(onClash: 'skip' | 'replace'): void {
    opts.applyImport(importPlan.value.importing, onClash);
    cancelImport();
  }

  function cancelImport(): void {
    importOpen.value = false;
    importResult.value = null;
    importFileError.value = null;
  }

  return {
    exportText,
    downloadCsv,
    downloadTemplate,
    importOpen,
    importLoading,
    importFileError,
    importPlan,
    importErrors,
    importIgnoredVpRows,
    importTotalHours,
    importClashCount,
    importClashHours,
    importFile,
    applyImportPlan,
    cancelImport,
  };
}
